import React from 'react';

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{ marginBottom: 40 }}>
    <h2 style={{ fontSize: '1.3rem', fontWeight: 700, marginBottom: 16, color: '#f9fafb' }}>{title}</h2>
    <div style={{ color: '#94a3b8', lineHeight: 1.8, fontSize: '0.95rem' }}>{children}</div>
  </div>
);

const RefundPage: React.FC = () => {
  return (
    <main style={{ paddingTop: 100 }}>
      <div className="container" style={{ maxWidth: 800, margin: '0 auto', padding: '60px 24px 80px' }}>
        <div style={{ marginBottom: 48 }}>
          <h1 style={{ fontSize: 'clamp(1.8rem, 4vw, 2.5rem)', fontWeight: 900, marginBottom: 12 }}>Refund Policy</h1>
          <p style={{ color: '#64748b', fontSize: '0.9rem' }}>Last updated: May 2026</p>
        </div>

        <div style={{
          background: 'rgba(34,197,94,0.08)',
          border: '1px solid rgba(34,197,94,0.25)',
          borderRadius: 12,
          padding: 20,
          marginBottom: 40,
          color: '#86efac',
          fontSize: '0.9rem',
          lineHeight: 1.7,
        }}>
          <strong>✅ We stand behind PRETTIFAI.</strong> If you're not satisfied, contact us within 14 days of your first payment and we'll make it right.
        </div>

        <Section title="1. Our Commitment">
          <p>PRETTIFAI is committed to customer satisfaction. Payments are processed by Paddle.com, our Merchant of Record. Paddle handles all refund requests in accordance with their refund policy and applicable consumer protection laws.</p>
        </Section>

        <Section title="2. Credit Packs">
          <p style={{ marginBottom: 12 }}>PRETTIFAI operates on a pay-as-you-go model — there are no subscriptions or recurring charges. All purchases are one-time credit packs.</p>
          <ul style={{ paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <li><strong style={{ color: '#f9fafb' }}>14-day money-back guarantee</strong> on your first pack. If you are unsatisfied for any reason within 14 days of your first purchase, contact us for a full refund.</li>
            <li>After 14 days, or once any credits have been used, the pack is non-refundable.</li>
            <li>Credits never expire — unused credits remain in your account indefinitely.</li>
          </ul>
        </Section>

        <Section title="3. Auto Top-Up">
          <p>If you have Auto Top-Up enabled and an automatic charge occurs, the same 14-day / zero-usage policy applies. If no credits from the auto-purchased pack have been used, you may request a refund within 14 days of the charge. Disable Auto Top-Up at any time from your account settings to prevent future automatic charges.</p>
        </Section>

        <Section title="4. Eligibility for Refunds">
          <p style={{ marginBottom: 12 }}>Refunds may be considered in the following circumstances:</p>
          <ul style={{ paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <li>You were charged for a pack you did not intend to purchase</li>
            <li>A technical error caused an incorrect charge</li>
            <li>You experienced duplicate charges</li>
            <li>The Service was materially unavailable for more than 72 consecutive hours after your purchase</li>
          </ul>
          <p style={{ marginTop: 12 }}>Refunds are not available for:</p>
          <ul style={{ paddingLeft: 20, marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <li>Packs where any credits have already been consumed</li>
            <li>Dissatisfaction with AI extraction accuracy after the 14-day money-back period</li>
            <li>Auto Top-Up charges where credits have already been used</li>
          </ul>
        </Section>

        <Section title="5. EU / EEA / UK Consumer Rights">
          <p style={{ marginBottom: 12 }}>If you are a consumer residing in the European Union, European Economic Area, or United Kingdom, you have a statutory right to withdraw from your purchase within <strong style={{ color: '#f9fafb' }}>14 days</strong> of the transaction date and receive a full refund — no reason required.</p>
          <p>This right does not apply once you have started consuming credits from the purchased pack, as by beginning to use the digital content you expressly consent to waive your right of withdrawal. If you have not used any credits, you are entitled to a full refund within 14 days of purchase.</p>
          <p style={{ marginTop: 12 }}>To exercise this right, please contact us using the method described in Section 6 below. Refunds are processed by Paddle (our Merchant of Record) in accordance with applicable consumer protection laws.</p>
        </Section>

        <Section title="6. How to Request a Refund">
          <p style={{ marginBottom: 12 }}>To request a refund:</p>
          <ol style={{ paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <li>Submit a <a href="/contact" style={{ color: "#60a5fa" }}>Refund Request</a> via our contact form (select Refund Request)</li>
            <li>Include your registered email address and reason for the refund</li>
            <li>We will respond within 2 business days</li>
            <li>Approved refunds are processed by Paddle within 5–10 business days</li>
          </ol>
        </Section>

        <Section title="7. Chargebacks">
          <p>We encourage you to contact us before initiating a chargeback with your bank. Chargebacks that are found to be fraudulent may result in immediate account termination and may be contested. Most disputes can be resolved quickly through direct communication.</p>
        </Section>

        <Section title="8. Exceptions and Fraud Prevention">
          <p>PRETTIFAI reserves the right to refuse refunds in cases of suspected abuse, including but not limited to: signing up multiple times to take advantage of money-back guarantees, using the Service to extract data and then requesting a refund, or any fraudulent activity.</p>
        </Section>

        <Section title="9. Jurisdiction and Consumer Rights">
          <p>This policy does not limit your statutory consumer rights under applicable law. In particular, residents of the European Union may have additional rights under the EU Consumer Rights Directive. If you believe you have rights beyond those described in this policy, please contact us.</p>
        </Section>

        <Section title="10. Contact">
          <p>
            For refund requests or billing questions:<br />
            <a href="/contact" style={{ color: "#60a5fa" }}>Contact Form</a>
          </p>
          <p style={{ marginTop: 12 }}>
            For urgent billing issues, Paddle can also be contacted directly through their customer support portal at <a href="https://www.paddle.com" style={{ color: '#60a5fa' }} target="_blank" rel="noopener noreferrer">paddle.com</a>.
          </p>
        </Section>
      </div>
    </main>
  );
};

export default RefundPage;
